import {
  ISVGSettings,
  SVGRowSettings,
  SVGSettingsNestedKey,
} from '@/pages/Dashboards/Charts/SVGChart/svgChart.types.ts'
import useSVGPreview from '@/pages/Dashboards/Charts/SVGChart/hooks/useSvgPreview.ts'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import { ITab } from '@/shared/Tabs/tabs.types.ts'
import Tabs from '@/shared/Tabs'
import { Fragment, useCallback, useState } from 'react'
import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import SvgChartSettingsTextRow from '@/pages/Dashboards/Charts/SVGChart/SvgChartSettingsSidePage/SVGChartSettingsContent/FormRows/SvgChartSettingsTextRow'
import SeparateLine from '@/shared/Forms/SeparateLine'
import useNodeOutline from '@/pages/Dashboards/Charts/SVGChart/hooks/useSvgNodeOutline.ts'
import SVGChartColorSettingsRow from '@/pages/Dashboards/Charts/SVGChart/SvgChartSettingsSidePage/SVGChartSettingsContent/FormRows/SVGChartColorSettingsRow'
import SvgChartVisibilitySettingsRow from '@/pages/Dashboards/Charts/SVGChart/SvgChartSettingsSidePage/SVGChartSettingsContent/FormRows/SvgChartVisibilitySettingsRow'
import SvgChartMovementSettingsRow from '@/pages/Dashboards/Charts/SVGChart/SvgChartSettingsSidePage/SVGChartSettingsContent/FormRows/SvgChartMovementSettingsRow'
import SvgChartRotationsSettingsRow from '@/pages/Dashboards/Charts/SVGChart/SvgChartSettingsSidePage/SVGChartSettingsContent/FormRows/SvgChartRotationsSettingsRow'
import svgSettingsPreviewService from '@/pages/Dashboards/Charts/SVGChart/svgSettings.preview.service.ts'

interface IProps {
  settings: ISVGSettings

  handleCreateNewNestedElement: (key: SVGSettingsNestedKey) => void
  handleNestedElementUpdate: (index: number, value: any, key: string, nestedKey: SVGSettingsNestedKey) => void
  handleNestedElementDelete: (index: number, nestedKey: SVGSettingsNestedKey) => void
}

const tabs: ITab<SVGSettingsNestedKey>[] = [
  { title: 'Текст', id: 'text' },
  { title: 'Цвет', id: 'color' },
  { title: 'Видимость', id: 'visibility' },
  { title: 'Движение', id: 'movements' },
  { title: 'Вращения', id: 'rotations' },
]

const SvgChartSettingsSecondStep = (props: IProps) => {
  const [tab, setTab] = useState<SVGSettingsNestedKey>('text')
  const previewCtrl = useSVGPreview({ open: true, documentId: props.settings.image_id })
  const outlineNode = useNodeOutline(previewCtrl.imageContainer)

  const handleRowPreview = useCallback((row: SVGRowSettings, category: SVGSettingsNestedKey) => {
    svgSettingsPreviewService.run(row, category, previewCtrl.imageContainer.current)
  }, [])

  const renderSettingsRows = () => {
    switch (tab) {
      case 'text':
        return props.settings.text.map((t, ind) => (
          <Fragment key={ind}>
            <SvgChartSettingsTextRow
              index={ind}
              settings={t}
              onChange={props.handleNestedElementUpdate}
              onDelete={props.handleNestedElementDelete}
              textNodeOptions={previewCtrl.parsedData.textOptions}
              onNodeOptionHover={outlineNode}
            />
            <SeparateLine />
          </Fragment>
        ))
      case 'color':
        return props.settings.color.map((t, ind) => (
          <Fragment key={ind}>
            <SVGChartColorSettingsRow
              onPlay={handleRowPreview}
              index={ind}
              settings={t}
              onChange={props.handleNestedElementUpdate}
              onDelete={props.handleNestedElementDelete}
              nodeOptions={previewCtrl.parsedData.nodeOptions}
              onNodeOptionHover={outlineNode}
            />
            <SeparateLine />
          </Fragment>
        ))
      case 'visibility':
        return props.settings.visibility.map((t, ind) => (
          <Fragment key={ind}>
            <SvgChartVisibilitySettingsRow
              onPlay={handleRowPreview}
              index={ind}
              settings={t}
              onChange={props.handleNestedElementUpdate}
              onDelete={props.handleNestedElementDelete}
              nodeOptions={previewCtrl.parsedData.nodeOptions}
              onNodeOptionHover={outlineNode}
            />
            <SeparateLine />
          </Fragment>
        ))
      case 'movements':
        return props.settings.movements.map((t, ind) => (
          <Fragment key={ind}>
            <SvgChartMovementSettingsRow
              onPlay={handleRowPreview}
              index={ind}
              settings={t}
              onChange={props.handleNestedElementUpdate}
              onDelete={props.handleNestedElementDelete}
              nodeOptions={previewCtrl.parsedData.nodeOptions}
              onNodeOptionHover={outlineNode}
            />
            <SeparateLine />
          </Fragment>
        ))
      case 'rotations':
        return props.settings.rotations.map((t, ind) => (
          <Fragment key={ind}>
            <SvgChartRotationsSettingsRow
              onPlay={handleRowPreview}
              index={ind}
              settings={t}
              onChange={props.handleNestedElementUpdate}
              onDelete={props.handleNestedElementDelete}
              nodeOptions={previewCtrl.parsedData.nodeOptions}
              onNodeOptionHover={outlineNode}
            />
            <SeparateLine />
          </Fragment>
        ))
    }
  }

  return (
    <div className="inline-flex-gap height100" style={{ minHeight: 0 }}>
      <div
        style={{
          height: '100%',
          borderRight: '1px solid var(--border-secondary-color)',
          paddingRight: '12px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            border: '1px solid var(--border-secondary-color)',
            padding: '8px',
            minHeight: '200px',
          }}
        >
          {previewCtrl.imageLoading && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%)' }}>
              <ThreeDotsLoader inscription="Загрузка..." />
            </div>
          )}
          <div
            style={{ width: '270px', flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            ref={previewCtrl.imageContainer}
          />
        </div>
      </div>

      <div className="flex-lines gap16 width100" style={{ minHeight: 0 }}>
        <Tabs tabs={tabs} onSelect={setTab as any} selectedTab={tab} style={{ width: '100%' }} />
        <div
          className="flex-lines gap16"
          style={{
            paddingBottom: '16px',
            overflow: 'auto',
            minHeight: 0,
            paddingRight: '24px',
            flex: 1,
            paddingLeft: '2px',
          }}
        >
          {renderSettingsRows()}
          <CreateEntityButton
            type="default"
            fullWidth={true}
            onClick={() => props.handleCreateNewNestedElement(tab)}
            text="Добавить узел"
          />
        </div>
      </div>
    </div>
  )
}

export default SvgChartSettingsSecondStep
